/**
 * @file components/audit-tasks/phases/PhasePreparation.tsx
 * @description 階段 1：文件審查（序64/122 由「開案準備」改名）。
 *
 * 結構：派案確認（接收案件） + 文件審查記錄 + 客戶基本資料 +
 *       客戶完整檔案 + 應附文件（序122：稽核員可上傳/替換/刪除） + 稽核團隊。
 *
 * 派案確認（序63 / 序64）：稽核員收派案後在此接收或不接收案件——
 *  - 接收：填【預定稽核日期】【稽核總人天數（含現場稽核 / 準備及報告）】、指定小組角色、簽名；
 *  - 不接收：填【驗證組備註】說明原因。兩者皆通知管理組。
 *
 * 預計稽核日期（序64）：主入口已遷至上方派案確認；本階段 SchedulePanel 保留
 * 「改期（序63.4，可新增多筆後續日期並通知管理組）+ 歷史時間線」功能。
 *
 * 開案文件審查（客戶 PPT S38）：稽核員填【文件審查紀錄】→ 判定通過 / 需補件；
 * 需補件時自動產生【文件審查通知單】並通知管理組轉達客戶，補件上傳後可再審至通過。
 */

import { Card, Descriptions, Space, Tag, Typography } from 'antd';
import type { AuditTaskDetail } from '@zhcb/shared';
import { DispatchConfirmPanel } from '@/components/audit-tasks/DispatchConfirmPanel';
import { DocReviewPanel } from '@/components/audit-tasks/DocReviewPanel';
import { ClientFullProfileCard } from '@/components/audit-tasks/preparation/ClientFullProfileCard';
import { CaseDocumentsCard } from '@/components/audit-tasks/preparation/CaseDocumentsCard';

interface Props {
  task: AuditTaskDetail;
}

export function PhasePreparation({ task }: Props) {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 派案確認（接收/不接收案件；序63 / 序64 預計日期在此填） */}
      <DispatchConfirmPanel task={task} />

      {/* 開案文件審查（多輪審查 / 補件通知，客戶 PPT S38） */}
      <DocReviewPanel task={task} />

      <Card size="small" title="被稽核組織">
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="客戶編號">
            {task.clientCode ? (
              <span style={{ fontFamily: 'monospace' }}>{task.clientCode}</span>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="客戶名稱">{task.clientName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="驗證方案">
            {task.caseScheme ? <Tag>{task.caseScheme}</Tag> : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="驗證類型">{task.caseVerificationType ?? '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {task.clientId && <ClientFullProfileCard clientId={task.clientId} />}

      <CaseDocumentsCard caseId={task.caseId} />

      <Card size="small" title={`稽核團隊（${task.teammates.length}）`}>
        {task.teammates.length === 0 ? (
          <Typography.Text type="secondary">尚未派遣團隊</Typography.Text>
        ) : (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {task.teammates.map((m) => (
              <div
                key={m.userId}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}
              >
                <Space>
                  <span>{m.userName ?? `#${m.userId}`}</span>
                  <Tag>{m.role}</Tag>
                </Space>
                {m.confirmedAt ? (
                  <Typography.Text type="success" style={{ fontSize: 12 }}>
                    已簽名確認
                  </Typography.Text>
                ) : (
                  <Typography.Text type="warning" style={{ fontSize: 12 }}>
                    未確認
                  </Typography.Text>
                )}
              </div>
            ))}
          </Space>
        )}
      </Card>
    </Space>
  );
}
